import { useContext, useMemo, useState } from "react";
import { LanguageContext } from "../../../context/LanguageContext";
import StepProgressBar from "../../../Libraries/StepProgressBar";
import { UBButton, ParagraphText } from "../../../Libraries";
import { Box } from "@mui/material";
import { useAuthContext } from "../../../context/AuthContext";
import { getPrint } from "../../../constants";
import styles from "./styles.module.css";
import {
  NewCheckMarkRoundIcon,
  PlayIcon,
  RefreshNewIcon,
} from "../../../assets/icons/icon";
import ConfirmationModal from "../../common/Modals/ConfirmationModal/index.js";
import { VideoModal } from "../../common/Modals/index.js";
import TabAutoComplete from "../../../Libraries/TabAutoComplete/index.js";
import { Sync } from "../../../SyncService/index.js";
import { analytics, triggerLogEvent } from "../../../config/firebase.js";
import { useNavigate } from "react-router-dom";
import { checkConnection } from "../../../printerConnection/index.js";
import { useSnackbar } from "../../../context/SnackbarContext.js";
import ChatwootWidget from "../../common/Chatwoot/index.js";
import { openChatwootWindow } from "../../../utils/chatwoot.js";

const DOWNLOAD_LINKS = [
  "https://udhaar-uploads-aws.s3.ap-southeast-1.amazonaws.com/java.exe",
  "https://udhaar-uploads-aws.s3.ap-southeast-1.amazonaws.com/printer-software.exe",
  "https://udhaar-uploads-aws.s3.ap-southeast-1.amazonaws.com/printer-driver.exe",
];

export const PrinterSteps = ({
  listOfPrinters,
  setListOfPrinters,
  setViewMode,
  activeStep,
  setActiveStep,
  setLoading,
}) => {
  const [I18n] = useContext(LanguageContext);
  const { business, user } = useAuthContext();
  const { showSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const [installStep, setInstallStep] = useState(1);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [isShowConfirmationModal, setIsShowConfirmationModal] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [printer, setPrinter] = useState("");
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [error, setError] = useState("");
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);

  const steps = [
    { label: I18n.download_applications },
    { label: I18n.printer_selection },
    { label: I18n.test_printer },
  ];

  const MODAL_TITLES = {
    0: I18n.is_java_application_successfully_installed,
    1: I18n.is_printer_software_successfully_installed,
    2: I18n.is_printer_software_drivers_successfully_installed,
    3: I18n.is_printer_test_successful,
  };

  const handleDownload = () => {
    const url = DOWNLOAD_LINKS[installStep];
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setHasDownloaded(true);
  };

  const handleRefereshFetchPrinters = async () => {
    setLoading(true);
    try {
      const res = await checkConnection(business);
      const printers = res?.[1] || [];
      setListOfPrinters(printers);
    } catch (e) {
      showSnackbar({
        message: e || "Error fetching printers",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrinter = () => {
    if (!printer) {
      setError(I18n.please_select_a_printer);
      return;
    }

    business
      .setPrinter(printer)
      .then(() => {
        setSelectedPrinter(null);
        setActiveStep((prev) => prev + 1);
        Sync(user.phone_number, navigate);
        triggerLogEvent(analytics, "printer_connected", { type: printer });
      })
      .catch(() => {
        setError(I18n.failed_to_connect_printer_please_try_again);
      });
  };

  const handleNextInstall = () => {
    if (!hasDownloaded && activeStep == 0) return;

    setHasDownloaded(false);
    setIsShowConfirmationModal(false);

    if (installStep < 2 && activeStep == 0) {
      setInstallStep((p) => p + 1);
    } else {
      if (activeStep == 2) {
        handlePrinter();
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleNextConfirm = () => {
    setIsShowConfirmationModal(true);
  };

  const handleAddPrinter = () => {
    if (!selectedPrinter) {
      setError(I18n.please_select_a_printer);
      return;
    }

    setPrinter(selectedPrinter?.value);
    setActiveStep(2);
  };

  const handleTestPrint = () => {
    if (!printer) return;

    const data = {
      date: new Date(),
      items: [{ name: "Test Item", quantity: 1, selling_price: 100 }],
    };
    getPrint({ ...data, printer, business });
    setIsShowConfirmationModal(true);
  };

  const handleButtonClick = () => {
    if (activeStep === 0) {
      if (!hasDownloaded) {
        handleDownload();
      } else {
        handleNextConfirm();
      }
      return;
    }

    if (activeStep === 1) {
      handleAddPrinter();
      return;
    }

    if (activeStep === 2) {
      handleTestPrint();
      return;
    }

    if (activeStep === 3) {
      setViewMode("printers");
    }
  };

  const title = useMemo(() => {
    if (activeStep === 0) {
      const titles = [
        I18n.to_setup_printer_download_java,
        I18n.download_and_install_printer_software,
        I18n.download_and_install_drivers,
      ];
      return titles[installStep];
    }

    if (activeStep === 1) return I18n.printer_selection;
    if (activeStep === 2) return I18n.test_selected_printer;

    return I18n.printer_setup_successfully;
  }, [activeStep, installStep]);

  const buttonText = useMemo(() => {
    if (activeStep === 0) {
      if (!hasDownloaded) {
        if (installStep === 0) return I18n.download_java;
        if (installStep === 1) return I18n.download_software;
        if (installStep === 2) return I18n.download_drivers;
      }
      return "Next";
    }

    if (activeStep === 1) return I18n.confirm;
    if (activeStep === 2) return I18n.test_print;

    return "Done";
  }, [activeStep, installStep, hasDownloaded]);

  const formattedPrinters = useMemo(() => {
    return (
      listOfPrinters?.map((printerName) => ({
        label: printerName,
        value: printerName,
      })) || []
    );
  }, [listOfPrinters]);

  const isDisabled = useMemo(() => {
    if (activeStep == 1 && !selectedPrinter) return true;
    return false;
  }, [activeStep, selectedPrinter]);

  const isPrinterListEmpty = useMemo(() => {
    return activeStep === 1 && formattedPrinters?.length === 0;
  }, [activeStep, formattedPrinters]);

  return (
    <div className={styles.root}>
      <div className={styles.stepperWrapper}>
        <StepProgressBar steps={steps} activeStep={activeStep} />
      </div>

      <div className={styles.contentWrapper}>
        <Box
          className={`${styles.descWrapper} ${activeStep > 2 ? styles.successWrapper : ""}`}
        >
          {activeStep == 3 ? (
            <NewCheckMarkRoundIcon width="60px" height="60px" />
          ) : null}

          <p className={styles.description}>{title}</p>

          {activeStep == 1 || activeStep == 2 ? (
            <TabAutoComplete
              id="printer_select_input"
              list={
                formattedPrinters?.length
                  ? formattedPrinters
                  : [{ label: I18n.no_option, value: "" }]
              }
              handleSelect={(item) => {
                setSelectedPrinter(item);
                setError("");
              }}
              disabled={activeStep == 2}
              value={selectedPrinter?.value}
              placeholder={I18n.select_printer}
              error={error}
              className={isPrinterListEmpty ? styles.autoComplete : null}
            />
          ) : null}

          <UBButton
            id="printer_action_btn"
            title={<ParagraphText>{buttonText}</ParagraphText>}
            className={`buttonPrimary saveSaleButton ${styles.downloadButton} ${activeStep > 2 ? styles.successButton : ""}`}
            onClick={handleButtonClick}
            disabled={isDisabled}
          />
        </Box>

        {isPrinterListEmpty ? (
          <Box className={styles.printerErrorMessage}>
            <ParagraphText
              className={`fontSize14 fontWeight400 foundationRedNormalColor`}
            >
              Is your printer not present in the list? Install Java, the printer
              software, and the printer driver, then click on{""}
              <UBButton
                id="printer_refresh_btn"
                title={<ParagraphText>{I18n.refresh}.</ParagraphText>}
                className={`${styles.refreshButton}`}
                onClick={() => { triggerLogEvent(analytics, "printer_refresh"); handleRefereshFetchPrinters(); }}
                iconComponent={<RefreshNewIcon height="16px" width="16px" />}
              />
            </ParagraphText>
          </Box>
        ) : null}

        <div className={styles.helpLinks}>
          {/* <button
            id="printer_watch_video_btn"
            onClick={() => { triggerLogEvent(analytics, "printer_watch_video"); setIsVideoModalOpen(true); }}
            type="button"
            className={styles.videoLink}
          >
            <PlayIcon className={styles.linkIcon} />
            <span>{I18n.watch_video_for_help}</span>
          </button> */}

          <button
            id="printer_whatsapp_btn"
            onClick={() => { triggerLogEvent(analytics, "printer_whatsapp_support"); setIsChatWidgetOpen(true); openChatwootWindow(); }}
            type="button"
            className={styles.contactLink}
          >
            <span>
              {I18n.need_help}{" "}
              <span className={styles.number}>{I18n.chat_with_us}</span>
            </span>
          </button>
        </div>
        {isChatWidgetOpen ? <ChatwootWidget user={user} /> : null}
      </div>

      {isShowConfirmationModal && (
        <ConfirmationModal
          open={isShowConfirmationModal}
          onClose={() => {
            setHasDownloaded(false);
            setIsShowConfirmationModal(false);
          }}
          onConfirm={handleNextInstall}
          message={MODAL_TITLES[activeStep == 2 ? 3 : installStep]}
          confirmText={I18n.confirm}
          cancelText={I18n.back}
          confirmButtonClassName="ubPrimaryButton"
          customClassName={styles.confirmationModal}
          id="installation_confirmation_modal"
        />
      )}

      {isVideoModalOpen ? (
        <VideoModal
          open={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          title={I18n.one_click_sale_faster_transaction}
          subtitle={I18n.speed_up_sales_reduce_wait_times}
          youtubeEmbedUrl={"https://www.youtube.com/embed/ZcDRw-FOVtQ"}
          primaryButton={{
            title: I18n.proceed,
            onClick: () => setIsVideoModalOpen(false),
          }}
          secondaryButton={{ text: I18n.close }}
        />
      ) : null}
    </div>
  );
};
